import React, { useState, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Modal,
  Form,
  Upload,
  message,
  Card,
  Tag,
  Space,
  Image,
  Divider,
  Row,
  Col,
  Popconfirm,
  Typography,
  Descriptions,
  Badge,
  Select,
  Switch,
} from "antd";
import {
  SearchOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined
} from "@ant-design/icons";
import axios from "axios";
import { getUserData } from "../../../hooks/Auth";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const BlogManagement = () => {
  const [blogs, setBlogs] = useState([]);
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [selectedBlog, setSelectedBlog] = useState(null);
  const [form] = Form.useForm();
  const [editingId, setEditingId] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [sectionFiles, setSectionFiles] = useState({});
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [uploading, setUploading] = useState(false);
  const user = getUserData();
  
 const API_BASE_URL = "https://techcrew.pk/ulc/";
  const UPLOAD_URL = `${API_BASE_URL}Upload_BlogImage.php`;

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    filterBlogs();
  }, [blogs, searchText, statusFilter]);

  const fetchBlogs = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Blogs.php`);
      if (data.status && data.status.code === 200) {
        setBlogs(data.data);
      } else {
        message.error(data.status?.message || "Failed to fetch blog data");
      }
    } catch (error) {
      console.error("API Error:", error);
      message.error("Failed to connect to server");
    } finally {
      setLoading(false);
    }
  };

  const filterBlogs = () => {
    let filtered = [...blogs];
    if (statusFilter) {
      filtered = filtered.filter((item) => item.status === statusFilter);
    }
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.title.toLowerCase().includes(searchLower) ||
          (item.subtitle && item.subtitle.toLowerCase().includes(searchLower))
      );
    }
    setFilteredBlogs(filtered);
  };

  const handleImageUpload = async (file, folder = "blogs") => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("folder", folder);

    try {
      const response = await axios.post(UPLOAD_URL, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        return response.data.filePath;
      } else {
        throw new Error(response.data.message);
      }
    } catch (error) {
      message.error("Image upload failed");
      throw error;
    }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await axios.post(`${API_BASE_URL}Delete_Blog.php`, {
        blog_id: id,
      });
      if (data.status.code === 200) {
        message.success("Blog deleted successfully");
        fetchBlogs();
      } else {
        throw new Error(data.message);
      }
    } catch (error) {
      message.error(error.message || "Deletion failed");
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setUploading(true);

      // Upload featured image
    let featuredImagePath = null;
    if (sectionFiles["featured"] && sectionFiles["featured"].originFileObj) {
      featuredImagePath = await handleImageUpload(sectionFiles["featured"].originFileObj, "blogs");
    } else if (sectionFiles["featured"] && sectionFiles["featured"].url) {
      featuredImagePath = sectionFiles["featured"].url.replace("https://techcrew.pk/ulc/", "");
    }

      // Process sections with images - UPLOAD ALL IMAGES FIRST
      const processedSections = [];
      
      for (let i = 0; i < values.sections.length; i++) {
        const section = values.sections[i];
        
        if (section.content_type === "image") {
          // Check if we have a new image to upload
          if (sectionFiles[i] && sectionFiles[i].originFileObj) {
  const imagePath = await handleImageUpload(sectionFiles[i].originFileObj, "blogs");
  processedSections.push({
    content_type: "image",
    image_path: imagePath,
    content: null
  });
}

          // Check if we have an existing image path (when editing)
          else if (section.image_path) {
            processedSections.push({
              content_type: "image",
              image_path: section.image_path,
              content: null
            });
          }
          // If no image is selected, skip this section
          else {
            message.warning(`Section ${i + 1} (Image) skipped - no image selected`);
            continue;
          }
        } else {
          // Text section
          processedSections.push({
            content_type: "text",
            content: section.content,
            image_path: null
          });
        }
      }

      // Prepare data for API
      const formData = {
        title: values.title,
        subtitle: values.subtitle || null,
        featured_image: featuredImagePath,
        sections: processedSections,
        status: values.status ? "Active" : "Inactive"
      };

      if (editingId) {
        formData.blog_id = editingId;
      }

      console.log("Sending data to API:", JSON.stringify(formData, null, 2));

      const response = await axios.post(
        `${API_BASE_URL}AddUpdate_Blog.php`,
        formData,
        { headers: { "Content-Type": "application/json" } }
      );

      console.log("API Response:", response.data);

      if (response.data.status.code === 200) {
        message.success(response.data.status.message);
        setModalVisible(false);
        setFileList([]);
        setSectionFiles({});
        fetchBlogs();
      } else {
        throw new Error(response.data.status.message);
      }
    } catch (error) {
      console.error("Submission error:", error);
      message.error(error.message || "Error saving blog");
    } finally {
      setUploading(false);
    }
  };

  const columns = [
    {
      title: "Blog",
      dataIndex: "title",
      key: "title",
      render: (text, record) => (
        <Space>
          {record.featured_image && (
            <Image
              src={`https://techcrew.pk/ulc/${record.featured_image}`}
              width={50}
              height={50}
              style={{ borderRadius: 4 }}
              preview={false}
            />
          )}
          <div>
            <Text strong>{text}</Text>
            <br />
            <Text type="secondary" ellipsis>{record.subtitle || "No subtitle"}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Sections",
      dataIndex: "sections",
      key: "sections",
      render: (sections) => (
        <div>
          <Tag color="blue">{sections?.length || 0} sections</Tag>
          {sections && sections.some(s => s.content_type === "image") && (
            <Tag color="green">{sections.filter(s => s.content_type === "image").length} images</Tag>
          )}
        </div>
      ),
      align: "center",
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created",
      render: (date) => new Date(date).toLocaleDateString(),
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Badge
          status={status === "Active" ? "success" : "error"}
          text={status}
        />
      ),
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedBlog(record);
              setViewModalVisible(true);
            }}
          />
          <Button
            icon={<EditOutlined />}
            onClick={() => {
              setEditingId(record.blog_id);
              form.setFieldsValue({
                title: record.title,
                subtitle: record.subtitle,
                status: record.status === "Active",
                sections: record.sections || []
              });

              // In the edit button onClick handler:
if (record.featured_image) {
  setSectionFiles(prev => ({
    ...prev,
    "featured": {
      uid: '-1',
      name: record.featured_image.split("/").pop(),
      status: 'done',
      url: `https://techcrew.pk/ulc/${record.featured_image}`,
    }
  }));
} else {
  setSectionFiles(prev => ({ ...prev, "featured": null }));
}

              const sectionFilesObj = {};
              if (record.sections) {
                record.sections.forEach((section, index) => {
                  if (section.content_type === "image" && section.image_path) {
                    sectionFilesObj[index] = {
                      uid: `section-${index}`,
                      name: section.image_path.split("/").pop(),
                      status: 'done',
                      url: `https://techcrew.pk/ulc/${section.image_path}`,
                    };
                  }
                });
              }
              setSectionFiles(sectionFilesObj);
              setModalVisible(true);
            }}
          />
          <Popconfirm
            title="Delete this blog?"
            onConfirm={() => handleDelete(record.blog_id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} danger />
          </Popconfirm>
        </Space>
      ),
      align: "center",
    },
  ];

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4}>Blog Management</Title>}
        bordered={false}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              form.resetFields();
              setEditingId(null);
              setFileList([]);
              setSectionFiles({});
              form.setFieldsValue({ 
                status: true,
                sections: [{ content_type: "text", content: "" }] 
              });
              setModalVisible(true);
            }}
          >
            Add Blog
          </Button>
        }
      >
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={12}>
            <Input
              placeholder="Search blogs..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
            />
          </Col>
          <Col span={12}>
            <Select
              style={{ width: "100%" }}
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
                { value: "", label: "All Statuses" },
              ]}
            />
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={filteredBlogs}
          rowKey="blog_id"
          loading={loading}
          scroll={{ x: true }}
          bordered
        />
      </Card>

      <Modal
        title={editingId ? "Edit Blog" : "Add New Blog"}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => {
          setModalVisible(false);
          setFileList([]);
          setSectionFiles({});
        }}
        width={800}
        okText={editingId ? "Update" : "Add"}
        confirmLoading={uploading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="title"
            label="Blog Title"
            rules={[{ required: true, message: "Please enter title" }]}
          >
            <Input placeholder="Enter blog title" />
          </Form.Item>

          <Form.Item name="subtitle" label="Subtitle">
            <Input placeholder="Enter blog subtitle (optional)" />
          </Form.Item>

          <Form.Item label="Featured Image">
  <Upload
    listType="picture-card"
    fileList={sectionFiles["featured"] ? [sectionFiles["featured"]] : []}
    beforeUpload={() => false}
    onChange={({ fileList: newFileList }) => {
      setSectionFiles(prev => ({
        ...prev,
        "featured": newFileList[0] || null
      }));
    }}
    accept="image/*"
  >
    {!sectionFiles["featured"] && (
      <div>
        <PlusOutlined />
        <div style={{ marginTop: 8 }}>Upload</div>
      </div>
    )}
  </Upload>
</Form.Item>

          <Form.Item name="status" label="Status" valuePropName="checked" initialValue={true}>
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" defaultChecked />
          </Form.Item>

          <Divider>Blog Content Sections</Divider>

          <Form.List name="sections">
  {(fields, { add, remove }) => (
    <>
      {fields.map(({ key, name: fieldIndex, ...restField }) => (
        <div key={key} style={{ marginBottom: 16, padding: 16, border: "1px solid #d9d9d9", borderRadius: 6 }}>
          <Form.Item
            {...restField}
            name={[fieldIndex, "content_type"]}
            label="Content Type"
            rules={[{ required: true, message: "Select content type" }]}
          >
            <Select placeholder="Select content type">
              <Option value="text">Text Paragraph</Option>
              <Option value="image">Image</Option>
            </Select>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, curValues) =>
              prevValues.sections?.[fieldIndex]?.content_type !==
              curValues.sections?.[fieldIndex]?.content_type
            }
          >
            {({ getFieldValue }) => {
              const contentType = getFieldValue(["sections", fieldIndex, "content_type"]);

              if (contentType === "image") {
                return (
                  <Form.Item label="Image" required help="Please upload an image for this section">
                    <Upload
                      listType="picture-card"
                      fileList={sectionFiles[fieldIndex] ? [sectionFiles[fieldIndex]] : []}
                      beforeUpload={() => false}
                      onChange={({ fileList: newFileList }) => {
                        setSectionFiles((prev) => ({
                          ...prev,
                          [fieldIndex]: newFileList[0] || null,
                        }));
                      }}
                      accept="image/*"
                    >
                      {!sectionFiles[fieldIndex] && (
                        <div>
                          <PlusOutlined />
                          <div style={{ marginTop: 8 }}>Upload</div>
                        </div>
                      )}
                    </Upload>
                  </Form.Item>
                );
              }

              if (contentType === "text") {
                return (
                  <Form.Item
                    {...restField}
                    name={[fieldIndex, "content"]}
                    label="Paragraph Content"
                    rules={[{ required: true, message: "Please enter content" }]}
                  >
                    <TextArea rows={4} placeholder="Enter paragraph content" />
                  </Form.Item>
                );
              }

              return null;
            }}
          </Form.Item>

          <Button
            icon={<MinusCircleOutlined />}
            danger
            onClick={() => {
              remove(fieldIndex);
              const newSectionFiles = { ...sectionFiles };
              delete newSectionFiles[fieldIndex];
              setSectionFiles(newSectionFiles);
            }}
          >
            Remove Section
          </Button>
        </div>
      ))}

      <Form.Item>
        <Button
          type="dashed"
          onClick={() => add({ content_type: "text", content: "" })}
          block
          icon={<PlusCircleOutlined />}
        >
          Add Section
        </Button>
      </Form.Item>
    </>
  )}
</Form.List>

        </Form>
      </Modal>

      <Modal
        title="Blog Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={800}
      >
        {selectedBlog && (
          <>
            <Descriptions bordered column={1}>
              <Descriptions.Item label="Title">
                <Text strong>{selectedBlog.title}</Text>
              </Descriptions.Item>
              {selectedBlog.subtitle && (
                <Descriptions.Item label="Subtitle">
                  {selectedBlog.subtitle}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Status">
                <Badge
                  status={selectedBlog.status === "Active" ? "success" : "error"}
                  text={selectedBlog.status}
                />
              </Descriptions.Item>
            </Descriptions>

            {selectedBlog.featured_image && (
              <>
                <Divider>Featured Image</Divider>
                <Image
                  src={`https://techcrew.pk/ulc/${selectedBlog.featured_image}`}
                  style={{ width: '100%', marginBottom: 24 }}
                />
              </>
            )}

            <Divider>Content Sections</Divider>
            
            {selectedBlog.sections?.map((section, index) => (
              <div key={index} style={{ marginBottom: 24 }}>
                {section.content_type === 'text' ? (
                  <Paragraph>{section.content}</Paragraph>
                ) : (
                  <Image
                    src={`https://techcrew.pk/ulc/${section.image_path}`}
                    style={{ width: '100%' }}
                  />
                )}
                {index < selectedBlog.sections.length - 1 && <Divider dashed />}
              </div>
            ))}
          </>
        )}
      </Modal>
    </div>
  );
};

export default BlogManagement;